import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Mail, Send, Eye, Loader2, Plus, Trash2, GripVertical,
  Type, Image, ChevronUp, ChevronDown, Save, FileX
} from 'lucide-react';
import { useAdminEmail } from './AdminEmailContext';

const ComposeTab = () => {
  const ctx = useAdminEmail();
  const {
    formData, hasDraft, clearDraft, handleInputChange,
    contentBlocks, draggedIndex, handleDragStart, handleDragOver, handleDragEnd,
    moveBlock, removeBlock, updateBlock, addBlock,
    uploadingBlock, handleImageUpload,
    emailCategories, emailStats,
    handlePreview, handleSendTest, handleSendToAll,
    sending, sendingTest,
  } = ctx;

  return (
    <div className="compose-tab">
      <Card className="compose-card">
        <CardHeader>
          <div className="compose-header-row">
            <div>
              <CardTitle>Compose Announcement</CardTitle>
              <CardDescription>Create and send a feature announcement to your users</CardDescription>
            </div>
            {hasDraft && (
              <div className="draft-indicator">
                <Save size={14} />
                <span>Draft saved</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDraft}
                  className="clear-draft-btn"
                  data-testid="clear-draft"
                >
                  <FileX size={14} />
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Email Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="category-select"
                data-testid="email-category"
              >
                {emailCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Email Subject *</label>
              <Input
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="New Feature: AI-Powered Recommendations"
                data-testid="email-subject"
              />
            </div>

            <div className="form-group full-width">
              <label>Email Content *</label>
              <p className="field-hint">Add titles, text blocks, and images. Drag to reorder.</p>

              <div className="content-blocks">
                {contentBlocks.map((block, index) => (
                  <div
                    key={block.id}
                    className={`content-block ${block.type} ${draggedIndex === index ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    data-testid={`content-block-${index}`}
                  >
                    <div className="block-header">
                      <div className="block-drag-handle">
                        <GripVertical size={16} />
                      </div>
                      <div className="block-type-label">
                        {block.type === 'text' && <><Type size={14} /> Text Block</>}
                        {block.type === 'image' && <><Image size={14} /> Image</>}
                        {block.type === 'title' && <><Type size={14} /> Title</>}
                      </div>
                      <div className="block-controls">
                        <button
                          className="block-move-btn"
                          onClick={() => moveBlock(index, -1)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          className="block-move-btn"
                          onClick={() => moveBlock(index, 1)}
                          disabled={index === contentBlocks.length - 1}
                          title="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button
                          className="block-remove-btn"
                          onClick={() => removeBlock(block.id)}
                          title="Remove block"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="block-content">
                      {block.type === 'text' && (
                        <textarea
                          value={block.content}
                          onChange={(e) => updateBlock(block.id, e.target.value)}
                          placeholder={"Enter your text here...\n\nUse line breaks to format your message."}
                          rows={4}
                          className="block-textarea"
                          data-testid={`block-text-${index}`}
                        />
                      )}
                      {block.type === 'title' && (
                        <div className="title-block-input">
                          <div className="title-input-group">
                            <label>Main Title</label>
                            <Input
                              value={block.content}
                              onChange={(e) => updateBlock(block.id, e.target.value, 'content')}
                              placeholder="Your Main Heading"
                              className="title-main-input"
                              data-testid={`block-title-main-${index}`}
                            />
                          </div>
                          <div className="title-input-group">
                            <label>Sub Title (optional)</label>
                            <Input
                              value={block.subContent || ''}
                              onChange={(e) => updateBlock(block.id, e.target.value, 'subContent')}
                              placeholder="Optional subtitle"
                              className="title-sub-input"
                              data-testid={`block-title-sub-${index}`}
                            />
                          </div>
                        </div>
                      )}
                      {block.type === 'image' && (
                        <div className="image-block-input">
                          <div className="image-upload-options">
                            <label className="upload-btn" data-testid={`upload-image-${index}`}>
                              {uploadingBlock === block.id ? (
                                <Loader2 size={16} className="spinning" />
                              ) : (
                                <Plus size={16} />
                              )}
                              {uploadingBlock === block.id ? 'Uploading...' : 'Upload Image'}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={(e) => handleImageUpload(block.id, e.target.files[0])}
                                disabled={uploadingBlock === block.id}
                                style={{ display: 'none' }}
                              />
                            </label>
                            <span className="or-divider">or</span>
                            <Input
                              value={block.content}
                              onChange={(e) => updateBlock(block.id, e.target.value)}
                              placeholder="Paste image URL"
                              className="url-input"
                              data-testid={`block-image-url-${index}`}
                            />
                          </div>
                          {block.content && (
                            <div className="image-preview">
                              <img
                                src={block.content}
                                alt="Preview"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <button
                                className="remove-image-btn"
                                onClick={() => updateBlock(block.id, '')}
                                title="Remove image"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="add-block-buttons">
                <Button
                  variant="outline"
                  onClick={() => addBlock('title')}
                  className="add-block-btn"
                  data-testid="add-title-block"
                >
                  <Plus size={16} />
                  <Type size={16} />
                  Add Title
                </Button>
                <Button
                  variant="outline"
                  onClick={() => addBlock('text')}
                  className="add-block-btn"
                  data-testid="add-text-block"
                >
                  <Plus size={16} />
                  <Type size={16} />
                  Add Text
                </Button>
                <Button
                  variant="outline"
                  onClick={() => addBlock('image')}
                  className="add-block-btn"
                  data-testid="add-image-block"
                >
                  <Plus size={16} />
                  <Image size={16} />
                  Add Image
                </Button>
              </div>
            </div>

            <div className="form-group">
              <label>Button Text</label>
              <Input
                name="cta_text"
                value={formData.cta_text}
                onChange={handleInputChange}
                placeholder="Check it out"
                data-testid="email-cta-text"
              />
            </div>

            <div className="form-group">
              <label>Button URL</label>
              <Input
                name="cta_url"
                value={formData.cta_url}
                onChange={handleInputChange}
                placeholder="https://baad.tv/fresh-pastures"
                data-testid="email-cta-url"
              />
            </div>
          </div>

          <div className="action-buttons">
            <Button
              variant="outline"
              onClick={handlePreview}
              data-testid="preview-email"
            >
              <Eye size={16} />
              Preview
            </Button>
            <Button
              variant="outline"
              onClick={handleSendTest}
              disabled={sendingTest}
              data-testid="send-test-email"
            >
              {sendingTest ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail size={16} />
                  Send Test to Me
                </>
              )}
            </Button>
            <Button
              className="send-all-btn"
              onClick={handleSendToAll}
              disabled={sending || !emailStats?.recipients?.total_potential}
              data-testid="send-all-email"
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send to All ({emailStats?.recipients?.total_active || 0})
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComposeTab;
